/**
 * AdminService — API calls for ADMIN governance module.
 * 
 * ADMIN NO opera el negocio. Solo gobierna la plataforma:
 * - Suscripciones
 * - Pagos
 * - Seguridad / Accesos
 * - Dashboard de gobernanza
 * - Eliminación en cascada de bancas
 */

import { fetchAPI } from '../../../services/apiClient';

// ═══════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════

export interface DashboardGobernanza {
  bancas: { total: number; activas: number; inactivas: number };
  usuarios: { total: number; activos: number; inactivos: number };
  suscripciones: { total: number; activas: number; vencidas: number; proximas_a_vencer: number };
}

export interface SuscripcionItem {
  id: string;
  banca_id: string;
  banca_nombre: string;
  plan: string;
  fecha_inicio: string;
  fecha_vencimiento: string;
  estado: string;           // ACTIVA | SUSPENDIDA | VENCIDA
  monto_mensual: string;
  observacion: string;
}

export interface SuscripcionInput {
  banca_id: string;
  plan: string;
  fecha_inicio: string;
  fecha_vencimiento: string;
  monto_mensual: number;
  observacion?: string;
}

export interface SuscripcionUpdate {
  plan?: string;
  fecha_vencimiento?: string;
  estado?: string;
  monto_mensual?: number;
  observacion?: string;
}

export interface PagoItem {
  id: string;
  suscripcion_id: string;
  banca_id: string;
  banca_nombre: string;
  monto: string;
  fecha: string;
  metodo: string;
  observacion: string;
}

export interface PagoInput {
  suscripcion_id: string;
  banca_id: string;
  monto: number;
  fecha: string;
  metodo: string;
  observacion?: string;
}

export interface CascadeDeleteResult {
  banca_id: string;
  usuarios_eliminados: number;
  locales_eliminados: number;
  suscripciones_eliminadas: number;
  pagos_eliminados: number;
}

export interface BancaCreateInput {
  nombre: string;
  codigo: string;
  username: string;
  password: string;
  max_usuarios: number;
  max_locales: number;
}

export interface BancaLimitesUpdate {
  max_usuarios?: number;
  max_locales?: number;
  max_supervisores?: number;
  max_banqueros?: number;
  limite_apuesta_maxima?: number;
}

export interface SeguridadUsuario {
  id: string;
  username: string;
  nombre: string;
  role: string;
  activo: boolean;
  banca_id: string | null;
  banca_nombre: string;
  created_at: string | null;
}

export interface SeguridadResumen {
  por_rol: Array<{ role: string; total: number; activos: number }>;
  total_usuarios: number;
  total_bloqueados: number;
}

// ═══════════════════════════════════════════════════════
// API Calls
// ═══════════════════════════════════════════════════════

export const adminService = {
  // Dashboard
  getDashboard: () =>
    fetchAPI<DashboardGobernanza>('/admin/dashboard'),

  // Suscripciones
  getSuscripciones: () =>
    fetchAPI<SuscripcionItem[]>('/admin/suscripciones'),

  crearSuscripcion: (data: SuscripcionInput) =>
    fetchAPI<{ id: string; mensaje: string }>('/admin/suscripciones', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  actualizarSuscripcion: (id: string, data: SuscripcionUpdate) =>
    fetchAPI<{ mensaje: string }>(`/admin/suscripciones/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  // Pagos
  getPagos: (bancaId?: string) =>
    fetchAPI<PagoItem[]>(`/admin/pagos${bancaId ? `?banca_id=${bancaId}` : ''}`),

  registrarPago: (data: PagoInput) =>
    fetchAPI<{ id: string; mensaje: string }>('/admin/pagos', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Cascada
  eliminarBanca: (bancaId: string) =>
    fetchAPI<CascadeDeleteResult>(`/admin/bancas/${bancaId}`, {
      method: 'DELETE',
    }),

  // Crear banca
  crearBanca: (data: BancaCreateInput) =>
    fetchAPI<{ id: string; mensaje: string }>('/admin/bancas', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Actualizar límites de banca
  actualizarLimites: (bancaId: string, data: BancaLimitesUpdate) =>
    fetchAPI<{
      banca_id: string;
      max_usuarios: number;
      max_locales: number;
      max_supervisores?: number | null;
      max_banqueros?: number | null;
      limite_apuesta_maxima: number;
    }>(
      `/usuarios/admin/bancas/${bancaId}/limites`,
      { method: 'PATCH', body: JSON.stringify(data) },
    ),

  // Seguridad
  getSeguridadUsuarios: () =>
    fetchAPI<SeguridadUsuario[]>('/admin/seguridad/usuarios'),

  getSeguridadResumen: () =>
    fetchAPI<SeguridadResumen>('/admin/seguridad/resumen'),
};
